import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Input,
  HStack,
  VStack,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardHeader,
  CardBody,
  Grid,
  GridItem,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Flex,
  Divider,
  Stack,
  StackDivider,
  useColorModeValue,
  Icon,
  Tooltip,
  FormControl,
  FormLabel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Progress,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon, InfoIcon, DownloadIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';

const CaseDataViewer = () => {
  const { accessToken } = useContext(AppContext);
  const [caseId, setCaseId] = useState('');
  const [caseData, setCaseData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Colors for consistent styling - ALL color mode values at top level
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const highlightBg = useColorModeValue('blue.50', 'blue.900');
  const grayBg = useColorModeValue('gray.50', 'gray.800');

  // Load case data from the aggregator endpoint
  const loadCaseData = async () => {
    if (!caseId.trim()) {
      setError('Please enter a case ID');
      return;
    }

    setIsLoading(true);
    setError(null);
    setCaseData(null);

    try {
      const response = await callApi(`/cases/${caseId}/complete`, accessToken);
      const data = await response.json();

      if (response.ok) {
        setCaseData(data);
      } else {
        setError(data.error || 'Failed to load case data');
      }
    } catch (err) {
      console.error('Error loading case data:', err);
      setError('An error occurred while loading case data');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions for formatting
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(amount);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${Math.round(value * 100)}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getDaysAgo = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    return Math.floor((now - date) / (1000 * 60 * 60 * 24));
  };

  // Render the case summary bar
  const renderCaseSummaryBar = () => {
    if (!caseData || !caseData.case_info) return null;

    const caseInfo = caseData.case_info;
    const hasAnalysis = !!caseData.medical_necessity;
    const hasForecast = !!caseData.payment_forecast;
    const hasCoding = !!caseData.coding;
    const necessityScore = caseData.medical_necessity?.necessity_score;

    return (
      <Card mb={4} variant="filled" bg={highlightBg}>
        <CardBody>
          <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
            <GridItem>
              <Text fontSize="sm" fontWeight="bold">Case ID</Text>
              <Text>{caseInfo.external_case_id}</Text>
            </GridItem>
            <GridItem>
              <Text fontSize="sm" fontWeight="bold">Patient</Text>
              <Text>
                {caseInfo.patient_first_name} {caseInfo.patient_last_name}
              </Text>
            </GridItem>
            <GridItem>
              <Text fontSize="sm" fontWeight="bold">Procedure Date</Text>
              <Text>{formatDate(caseInfo.procedure_date)}</Text>
            </GridItem>
            <GridItem>
              <Text fontSize="sm" fontWeight="bold">Status</Text>
              <HStack spacing={2}>
                <Badge colorScheme={hasCoding ? 'green' : 'gray'}>
                  {hasCoding ? 'Coded' : 'Not Coded'}
                </Badge>
                <Badge colorScheme={hasAnalysis ? 'green' : 'gray'}>
                  {hasAnalysis ? 'Analyzed' : 'Not Analyzed'}
                </Badge>
                <Badge colorScheme={hasForecast ? 'green' : 'gray'}>
                  {hasForecast ? 'Forecasted' : 'No Forecast'}
                </Badge>
              </HStack>
            </GridItem>
            {necessityScore !== undefined && (
              <GridItem>
                <Text fontSize="sm" fontWeight="bold">Medical Necessity Score</Text>
                <Badge 
                  colorScheme={necessityScore >= 80 ? 'green' : necessityScore >= 50 ? 'yellow' : 'red'}
                  fontSize="md"
                  px={2}
                  py={1}
                >
                  {necessityScore}
                </Badge>
              </GridItem>
            )}
          </Grid>
        </CardBody>
      </Card>
    );
  };

  // Render Overview Tab
  const renderOverviewTab = () => {
    if (!caseData) return null;

    return (
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
        {/* Case Information */}
        <Card>
          <CardHeader>
            <Heading size="md">Case Information</Heading>
          </CardHeader>
          <CardBody>
            <Stack spacing={3}>
              <HStack justify="space-between">
                <Text fontWeight="bold">Case Type:</Text>
                <Text>{caseData.case_info?.case_type_id || 'N/A'}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontWeight="bold">Status:</Text>
                <Badge>{caseData.case_info?.status || 'Unknown'}</Badge>
              </HStack>
              <HStack justify="space-between">
                <Text fontWeight="bold">Created:</Text>
                <Text>{formatDate(caseData.case_info?.created_at)}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontWeight="bold">Updated:</Text>
                <Text>{formatDate(caseData.case_info?.updated_at)}</Text>
              </HStack>
            </Stack>
          </CardBody>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader>
            <Heading size="md">Quick Summary</Heading>
          </CardHeader>
          <CardBody>
            <Stack spacing={3}>
              <HStack justify="space-between">
                <Text fontWeight="bold">Coding Status:</Text>
                <Text>{caseData.coding?.status || 'Not Started'}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontWeight="bold">Medical Necessity:</Text>
                <Text>
                  {caseData.medical_necessity ? 
                    `Score: ${caseData.medical_necessity.necessity_score}` : 
                    'Not Analyzed'}
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontWeight="bold">Forecast Amount:</Text>
                <Text>
                  {caseData.payment_forecast ? 
                    formatCurrency(caseData.payment_forecast.forecasted_payment_amount) : 
                    'Not Available'}
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontWeight="bold">Financial Events:</Text>
                <Text>{caseData.financial_events?.length || 0} events</Text>
              </HStack>
            </Stack>
          </CardBody>
        </Card>

        {/* Carrier Information */}
        {caseData.carrier_info && (
          <Card>
            <CardHeader>
              <Heading size="md">Carrier Information</Heading>
            </CardHeader>
            <CardBody>
              <Stack spacing={3}>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Payer Type:</Text>
                  <Text>{caseData.carrier_info.payer_type}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Carrier:</Text>
                  <Text>{caseData.carrier_info.carrier_name || 'N/A'}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">State:</Text>
                  <Text>{caseData.carrier_info.state_abbr || 'N/A'}</Text>
                </HStack>
              </Stack>
            </CardBody>
          </Card>
        )}

        {/* Document Summary */}
        <Card>
          <CardHeader>
            <Heading size="md">Document Summary</Heading>
          </CardHeader>
          <CardBody>
            <Stack spacing={3}>
              {caseData.case_files && Object.keys(caseData.case_files).length > 0 ? (
                Object.entries(caseData.case_files).map(([category, files]) => (
                  <HStack key={category} justify="space-between">
                    <Text fontWeight="bold">{category}:</Text>
                    <Text>{files.length} files</Text>
                  </HStack>
                ))
              ) : (
                <Text color="gray.500">No documents uploaded</Text>
              )}
            </Stack>
          </CardBody>
        </Card>
      </Grid>
    );
  };

  // Enhanced Clinical Tab with Rich Data
  const renderClinicalTab = () => {
    if (!caseData) return null;

    return (
      <VStack spacing={6} align="stretch">
        {/* Medical Necessity Analysis - Enhanced */}
        <Box>
          <Heading size="lg" mb={4}>Medical Necessity Analysis</Heading>
          {caseData.medical_necessity ? (
            <Stack spacing={4}>
              {/* Summary Section */}
              <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                <Box>
                  <Text fontWeight="bold">Necessity Score:</Text>
                  <Badge colorScheme={caseData.medical_necessity.necessity_score >= 80 ? 'green' : 
                                    caseData.medical_necessity.necessity_score >= 60 ? 'yellow' : 'red'} size="lg">
                    {caseData.medical_necessity.necessity_score}
                  </Badge>
                </Box>
                <Box>
                  <Text fontWeight="bold">Review Status:</Text>
                  <Badge colorScheme={caseData.medical_necessity.needs_review ? 'orange' : 'green'}>
                    {caseData.medical_necessity.needs_review ? 'Needs Review' : 'Approved'}
                  </Badge>
                </Box>
                <Box>
                  <Text fontWeight="bold">Analysis Date:</Text>
                  <Text fontSize="sm">
                    {(() => {
                      const daysAgo = getDaysAgo(caseData.medical_necessity.created_at);
                      return daysAgo === 0 ? 'Today' : daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`;
                    })()}
                  </Text>
                </Box>
              </Grid>

              {/* Detailed Medical Necessity Analysis */}
              {caseData.medical_necessity.full_results?.analysis?.medical_necessity_analysis && (
                <Accordion allowToggle>
                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="bold">Detailed Medical Necessity Analysis</Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <VStack align="stretch" spacing={4}>
                        {caseData.medical_necessity.full_results.analysis.medical_necessity_analysis.overall_assessment && (
                          <Box>
                            <Text fontWeight="bold" mb={2}>Overall Assessment:</Text>
                            <Box p={3} bg={grayBg} borderRadius="md" borderLeft="4px solid" 
                                 borderLeftColor={caseData.medical_necessity.full_results.analysis.medical_necessity_analysis.overall_assessment.toLowerCase().includes("strong") ? "green.400" : 
                                                 caseData.medical_necessity.full_results.analysis.medical_necessity_analysis.overall_assessment.toLowerCase().includes("moderate") ? "yellow.400" : "red.400"}>
                              <Text>{caseData.medical_necessity.full_results.analysis.medical_necessity_analysis.overall_assessment}</Text>
                            </Box>
                          </Box>
                        )}
                        
                        {caseData.medical_necessity.full_results.analysis.medical_necessity_analysis.key_findings && (
                          <Box>
                            <Text fontWeight="bold" mb={2}>Key Findings:</Text>
                            <Box p={3} bg={grayBg} borderRadius="md">
                              <Text>{caseData.medical_necessity.full_results.analysis.medical_necessity_analysis.key_findings}</Text>
                            </Box>
                          </Box>
                        )}
                        
                        {caseData.medical_necessity.full_results.analysis.explanations?.medical_necessity && (
                          <Box>
                            <Text fontWeight="bold" mb={2}>Detailed Explanation:</Text>
                            <Box p={3} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.400">
                              <Text whiteSpace="pre-wrap">{caseData.medical_necessity.full_results.analysis.explanations.medical_necessity}</Text>
                            </Box>
                          </Box>
                        )}
                        
                        {caseData.medical_necessity.full_results.analysis.explanations?.payer_considerations && (
                          <Box>
                            <Text fontWeight="bold" mb={2}>Payer Considerations:</Text>
                            <Box p={3} bg="purple.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="purple.400">
                              <Text whiteSpace="pre-wrap">{caseData.medical_necessity.full_results.analysis.explanations.payer_considerations}</Text>
                            </Box>
                          </Box>
                        )}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )}

              {/* Modality Support Analysis */}
              {caseData.medical_necessity.full_results?.analysis?.modality_support && (
                <Card>
                  <CardHeader>
                    <Heading size="md">Modality Support Analysis</Heading>
                  </CardHeader>
                  <CardBody>
                    <SimpleGrid columns={[1, null, 2]} spacing={4}>
                      {Object.entries(caseData.medical_necessity.full_results.analysis.modality_support).map(([modality, details]) => (
                        <Box key={modality} p={4} borderWidth="1px" borderRadius="md" 
                             bg={details.support_level?.toLowerCase().includes('well') ? "green.50" : 
                                 details.support_level?.toLowerCase().includes('partial') ? "yellow.50" : "red.50"}>
                          <Text fontWeight="bold" mb={2}>{modality}</Text>
                          <Badge colorScheme={
                            details.support_level?.toLowerCase().includes('well') ? "green" : 
                            details.support_level?.toLowerCase().includes('partial') ? "yellow" : "red"
                          } mb={3}>
                            {details.support_level}
                          </Badge>
                          
                          {details.supporting_diagnoses && details.supporting_diagnoses.length > 0 && (
                            <Box mb={2}>
                              <Text fontSize="sm" fontWeight="bold" mb={1}>Supporting Diagnoses:</Text>
                              <HStack spacing={1} flexWrap="wrap">
                                {details.supporting_diagnoses.map((diagnosis, idx) => (
                                  <Badge key={idx} size="sm" colorScheme="blue">{diagnosis}</Badge>
                                ))}
                              </HStack>
                            </Box>
                          )}
                          
                          {details.recommendation && (
                            <Box>
                              <Text fontSize="sm" fontWeight="bold" mb={1}>Recommendation:</Text>
                              <Text fontSize="sm" color="gray.600">{details.recommendation}</Text>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </SimpleGrid>
                  </CardBody>
                </Card>
              )}

              {/* Missing Diagnosis Suggestions */}
              {caseData.medical_necessity.full_results?.missing_diagnosis_suggestions?.suggestions?.length > 0 && (
                <Card>
                  <CardHeader>
                    <Heading size="md">Missing Diagnosis Suggestions</Heading>
                  </CardHeader>
                  <CardBody>
                    {caseData.medical_necessity.full_results.missing_diagnosis_suggestions.summary && (
                      <Alert status="info" mb={4}>
                        <AlertIcon />
                        <Text>{caseData.medical_necessity.full_results.missing_diagnosis_suggestions.summary}</Text>
                      </Alert>
                    )}
                    
                    <VStack spacing={3} align="stretch">
                      {caseData.medical_necessity.full_results.missing_diagnosis_suggestions.suggestions
                        .sort((a, b) => (b.score || 0) - (a.score || 0))
                        .slice(0, 5)
                        .map((suggestion, index) => (
                        <Box key={index} p={4} borderWidth="1px" borderRadius="md" borderLeft="4px solid" 
                             borderLeftColor={suggestion.score >= 7 ? "green.400" : suggestion.score >= 4 ? "yellow.400" : "red.400"}>
                          <HStack justify="space-between" mb={2}>
                            <Text fontWeight="bold">{suggestion.code} - {suggestion.description}</Text>
                            {suggestion.score && (
                              <Badge colorScheme={suggestion.score >= 7 ? "green" : suggestion.score >= 4 ? "yellow" : "red"}>
                                Score: {suggestion.score}/10
                              </Badge>
                            )}
                          </HStack>
                          
                          <Text fontSize="sm" mb={2}>
                            <Text as="span" fontWeight="bold">Relevance to IONM:</Text> {suggestion.relevance_to_ionm}
                          </Text>
                          
                          {suggestion.potential_evidence && (
                            <Text fontSize="sm" mb={2}>
                              <Text as="span" fontWeight="bold">Potential Evidence:</Text> {suggestion.potential_evidence}
                            </Text>
                          )}
                          
                          <Text fontSize="sm" mb={2}>
                            <Text as="span" fontWeight="bold">Documentation Needed:</Text> {suggestion.documentation_needed}
                          </Text>
                          
                          {suggestion.applicable_modalities?.length > 0 && (
                            <Box>
                              <Text fontSize="sm" fontWeight="bold" mb={1}>Applicable Modalities:</Text>
                              <HStack spacing={2}>
                                {suggestion.applicable_modalities.map((modality, idx) => (
                                  <Badge key={idx} size="sm" colorScheme="purple">{modality}</Badge>
                                ))}
                              </HStack>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </Stack>
          ) : (
            <Alert status="info">
              <AlertIcon />
              No medical necessity analysis has been performed for this case.
            </Alert>
          )}
        </Box>

        {/* Coding Results */}
        <Card>
          <CardHeader>
            <Heading size="md">Coding Results</Heading>
          </CardHeader>
          <CardBody>
            {caseData.coding ? (
              <Stack spacing={4}>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Status:</Text>
                  <Badge colorScheme={caseData.coding.status === 'completed' ? 'green' : 'yellow'}>
                    {caseData.coding.status}
                  </Badge>
                </HStack>
                
                {caseData.coding.cpt_codes && caseData.coding.cpt_codes.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>CPT Codes:</Text>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Code</Th>
                          <Th>Description</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {caseData.coding.cpt_codes.map((code, idx) => (
                          <Tr key={idx}>
                            <Td>{code.code || code}</Td>
                            <Td>{code.description || 'N/A'}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                {caseData.coding.icd10_codes && caseData.coding.icd10_codes.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>ICD-10 Codes:</Text>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Code</Th>
                          <Th>Description</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {caseData.coding.icd10_codes.map((code, idx) => (
                          <Tr key={idx}>
                            <Td>{code.code || code}</Td>
                            <Td>{code.description || 'N/A'}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </Stack>
            ) : (
              <Alert status="info">
                <AlertIcon />
                No coding results available for this case.
              </Alert>
            )}
          </CardBody>
        </Card>
      </VStack>
    );
  };

  // Enhanced Financial Tab with Rich Data
  const renderFinancialTab = () => {
    if (!caseData) return null;

    return (
      <VStack spacing={6} align="stretch">
        {/* Enhanced Payment Forecast */}
        {caseData.payment_forecast && (
          <Box>
            <Heading size="lg" mb={4}>Payment Forecast</Heading>
            <Card>
              <CardHeader>
                <Heading size="md">Payment Forecast Analysis</Heading>
              </CardHeader>
              <CardBody>
                <Grid templateColumns="repeat(2, 1fr)" gap={6} mb={6}>
                  <Box>
                    <Text fontWeight="bold" mb={1}>Forecasted Amount:</Text>
                    <Text fontSize="2xl" color="blue.500" fontWeight="bold">
                      {formatCurrency(caseData.payment_forecast.forecasted_payment_amount)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" mb={1}>Payment Percentage:</Text>
                    <Text fontSize="2xl" color="blue.500" fontWeight="bold">
                      {formatPercentage(caseData.payment_forecast.forecasted_payment_percentage)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" mb={1}>Total Billed:</Text>
                    <Text fontSize="lg">{formatCurrency(caseData.payment_forecast.total_case_value_billed)}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" mb={1}>Confidence Score:</Text>
                    <VStack align="start" spacing={2}>
                      <Progress 
                        value={caseData.payment_forecast.forecast_confidence_score * 100} 
                        colorScheme={caseData.payment_forecast.forecast_confidence_score >= 0.7 ? "green" : 
                                    caseData.payment_forecast.forecast_confidence_score >= 0.4 ? "yellow" : "red"}
                        size="lg"
                        width="100%"
                        borderRadius="md"
                      />
                      <Badge colorScheme={caseData.payment_forecast.forecast_confidence_score >= 0.7 ? "green" : 
                                        caseData.payment_forecast.forecast_confidence_score >= 0.4 ? "yellow" : "red"} size="lg">
                        {formatPercentage(caseData.payment_forecast.forecast_confidence_score)} Confidence
                      </Badge>
                    </VStack>
                  </Box>
                </Grid>

                {caseData.payment_forecast.forecast_reasoning && (
                  <Accordion allowToggle>
                    <AccordionItem>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          <Text fontWeight="bold">AI Forecast Reasoning</Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <Box p={4} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.400">
                          <Text whiteSpace="pre-wrap" lineHeight="1.6">{caseData.payment_forecast.forecast_reasoning}</Text>
                        </Box>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                )}

                <Text fontSize="sm" color="gray.500" mt={4}>
                  <Text as="span" fontWeight="bold">Forecast Date:</Text> {formatDate(caseData.payment_forecast.forecast_date)}
                  {(() => {
                    const daysAgo = getDaysAgo(caseData.payment_forecast.forecast_date);
                    return daysAgo !== null ? ` (${daysAgo === 0 ? 'Today' : daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`})` : '';
                  })()}
                </Text>
              </CardBody>
            </Card>
          </Box>
        )}

        {!caseData.payment_forecast && (
          <Box>
            <Heading size="lg">Payment Forecast</Heading>
            <Alert status="info">
              <AlertIcon />
              No payment forecast has been generated for this case.
            </Alert>
          </Box>
        )}

        {/* Enhanced Financial Events */}
        {caseData.financial_events && caseData.financial_events.length > 0 && (
          <Box>
            <Heading size="lg" mb={4}>Financial Events</Heading>
            <Card>
              <CardHeader>
                <Heading size="md">Financial Events Timeline</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  {caseData.financial_events
                    .sort((a, b) => a.sequence_number - b.sequence_number)
                    .map((event) => (
                    <Box key={event.id} p={4} borderWidth="1px" borderRadius="md" 
                         borderLeft="4px solid" borderLeftColor="blue.400" bg={grayBg}>
                      <HStack justify="space-between" mb={3}>
                        <HStack spacing={3}>
                          <Badge colorScheme="blue" size="lg">Event #{event.sequence_number}</Badge>
                          <Badge variant="outline" colorScheme="gray">{event.response_type}</Badge>
                        </HStack>
                        <Text fontSize="sm" color="gray.500" fontWeight="bold">
                          {formatDate(event.response_received_date)}
                        </Text>
                      </HStack>
                      
                      <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={3}>
                        <Box>
                          <Text fontSize="sm" fontWeight="bold" color="gray.600">Billed:</Text>
                          <Text fontWeight="bold">{formatCurrency(event.total_billed_amount)}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" fontWeight="bold" color="gray.600">Paid:</Text>
                          <Text color="green.500" fontWeight="bold">{formatCurrency(event.total_paid_amount)}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" fontWeight="bold" color="gray.600">Denied:</Text>
                          <Text color="red.500" fontWeight="bold">{formatCurrency(event.total_denied_amount)}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" fontWeight="bold" color="gray.600">Payment %:</Text>
                          <Badge colorScheme={event.actual_payment_percentage >= 0.8 ? "green" : 
                                            event.actual_payment_percentage >= 0.5 ? "yellow" : "red"} size="lg">
                            {formatPercentage(event.actual_payment_percentage)}
                          </Badge>
                        </Box>
                      </Grid>
                      
                      {event.payer_decision_summary && (
                        <Box mb={3}>
                          <Text fontSize="sm" fontWeight="bold" mb={1}>Decision Summary:</Text>
                          <Box p={2} bg="gray.50" borderRadius="md">
                            <Text fontSize="sm">{event.payer_decision_summary}</Text>
                          </Box>
                        </Box>
                      )}
                      
                      {(event.denial_reason_codes?.length > 0 || event.denial_reason_text?.length > 0) && (
                        <Box p={3} bg="red.50" borderRadius="md" borderLeft="3px solid" borderLeftColor="red.400">
                          <Text fontSize="sm" fontWeight="bold" color="red.600" mb={2}>Denial Information:</Text>
                          {event.denial_reason_codes?.length > 0 && (
                            <Box mb={2}>
                              <Text fontSize="sm" fontWeight="bold" color="red.600">Codes:</Text>
                              <HStack spacing={1} mt={1}>
                                {event.denial_reason_codes.map((code, idx) => (
                                  <Badge key={idx} colorScheme="red" size="sm">{code}</Badge>
                                ))}
                              </HStack>
                            </Box>
                          )}
                          {event.denial_reason_text?.length > 0 && (
                            <Box>
                              <Text fontSize="sm" fontWeight="bold" color="red.600">Reasons:</Text>
                              <List spacing={1} mt={1}>
                                {event.denial_reason_text.map((reason, idx) => (
                                  <ListItem key={idx}>
                                    <ListIcon as={WarningIcon} color="red.500" />
                                    <Text as="span" fontSize="sm" color="red.600">{reason}</Text>
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          </Box>
        )}

        {(!caseData.financial_events || caseData.financial_events.length === 0) && (
          <Box>
            <Heading size="lg">Financial Events</Heading>
            <Alert status="info">
              <AlertIcon />
              No financial events recorded for this case.
            </Alert>
          </Box>
        )}
      </VStack>
    );
  };

  // Render Documents Tab
  const renderDocumentsTab = () => {
    if (!caseData) return null;

    const hasDocuments = caseData.case_files && Object.keys(caseData.case_files).length > 0;

    return (
      <VStack spacing={6} align="stretch">
        {hasDocuments ? (
          Object.entries(caseData.case_files).map(([category, files]) => (
            <Card key={category}>
              <CardHeader>
                <Heading size="md">{category} Documents</Heading>
              </CardHeader>
              <CardBody>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>File Name</Th>
                      <Th>Size</Th>
                      <Th>Uploaded</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {files.map((file) => (
                      <Tr key={file.id}>
                        <Td>{file.file_name}</Td>
                        <Td>{file.file_size ? `${(file.file_size / 1024).toFixed(2)} KB` : 'N/A'}</Td>
                        <Td>{formatDate(file.created_at)}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="Download file">
                              <Button size="xs" variant="outline" colorScheme="blue">
                                <Icon as={DownloadIcon} />
                              </Button>
                            </Tooltip>
                            {file.has_text && (
                              <Button size="xs" variant="outline">
                                View
                              </Button>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </CardBody>
            </Card>
          ))
        ) : (
          <Alert status="info">
            <AlertIcon />
            No documents have been uploaded for this case.
          </Alert>
        )}
      </VStack>
    );
  };

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        {/* Header with Case ID input */}
        <VStack spacing={6} align="stretch">
          <Flex justify="space-between" align="center">
            <Heading as="h1">Case Data Viewer</Heading>
            <HStack spacing={4}>
              <FormControl w="300px">
                <Input
                  placeholder="Enter Case ID"
                  value={caseId}
                  onChange={(e) => setCaseId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && loadCaseData()}
                />
              </FormControl>
              <Button
                colorScheme="blue"
                onClick={loadCaseData}
                isLoading={isLoading}
                loadingText="Loading..."
              >
                Load Case
              </Button>
            </HStack>
          </Flex>

          {/* Error Alert */}
          {error && (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>Error!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Loading State */}
          {isLoading && (
            <Flex justify="center" align="center" height="300px">
              <VStack>
                <Spinner size="xl" color="blue.500" />
                <Text mt={4}>Loading case data...</Text>
              </VStack>
            </Flex>
          )}

          {/* Case Summary Bar */}
          {caseData && !isLoading && renderCaseSummaryBar()}

          {/* Main Content Tabs */}
          {caseData && !isLoading && (
            <Tabs variant="enclosed" colorScheme="blue">
              <TabList>
                <Tab>Overview</Tab>
                <Tab>Clinical</Tab>
                <Tab>Financial</Tab>
                <Tab>Documents</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>{renderOverviewTab()}</TabPanel>
                <TabPanel>{renderClinicalTab()}</TabPanel>
                <TabPanel>{renderFinancialTab()}</TabPanel>
                <TabPanel>{renderDocumentsTab()}</TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default CaseDataViewer;
